import { throttle } from 'lodash';
import { useCallback, useEffect } from 'react';

import { useLazyGetIdentityQuery } from '../datasource/queries/identity';
import logger from '../utility/logger';
import { reloadPage } from '../utility/navigation';
import { useAppSelector } from '.';

const SECONDS_TO_THROTTLE_AUTH_CHECK = 5;

// A laptop wake fires visibilitychange the instant the tab becomes visible again, which is
// exactly when networking is still reconnecting — the very case a failed/indeterminate check is
// meant to tolerate. Without a scheduled retry, that viewer gets no further check until another
// visibility event, which may never come while they stay on the page. Bounded so a persistently
// unreachable identity endpoint can't retry forever.
const AUTH_CHECK_RETRY_DELAY_MS = 3000;
const AUTH_CHECK_MAX_RETRIES = 2;

export const useAuthCheck = () => {
    const user = useAppSelector((state) => state.user);
    const [getIdentity]  = useLazyGetIdentityQuery();

    /**
     * Returns whether the answer was non-definitive (failed check or indeterminate server
     * answer) — the caller uses this to decide whether a retry is worth scheduling. `false` means
     * either a healthy answer or a reload already in flight; nothing more to do.
     */
    const handleAuthCheck = useCallback(async (): Promise<boolean> => {
        // groupcast users don't need to be signed in
        if (user.isGroupcast) return false;

        let identity;

        try {
            identity = await getIdentity().unwrap();
        } catch (e) {
            // The check itself failed — a dropped connection, a timeout, a server blip. That says
            // nothing about whether the viewer is signed in, and this handler fires the moment a
            // laptop wakes up, which is exactly when the network is briefly absent. Reloading here
            // destroyed playback state over a non-answer; log it and let a scheduled retry re-ask.
            logger.log('Auth check could not be completed, keeping the session as-is', {
                individualSK: user.individualSK,
                reason: (e as Error).message,
            });
            return true;
        }

        // The server answered, but could not determine authorization because its own downstream
        // lookup failed. That is a server-side blip, not this viewer's state — treat it exactly
        // like a failed check and let a scheduled retry re-ask.
        if (identity.isIndeterminate) {
            logger.log('Auth check could not be completed, keeping the session as-is', {
                individualSK: user.individualSK,
                reason: 'Server identity lookup failed (indeterminate answer)',
            });
            return true;
        }

        const reason = !identity.isAuthorized
            ? 'User not authorized'
            : identity.individualSK != user.individualSK
                ? 'User individualSK is not valid anymore'
                : null;

        if (reason === null) return false;

        // A definitive answer: this viewer is genuinely signed out or is someone else now.
        logger.log('User signed out during playtime', {
            individualSK: user.individualSK,
            reason,
        });
        reloadPage();
        return false;
    }, [getIdentity, user.individualSK, user.isGroupcast]);

    useEffect(() => {
        let cancelled = false;
        let activeGeneration = 0;
        let retryTimeout: ReturnType<typeof setTimeout> | undefined;
        let flushTimeout: ReturnType<typeof setTimeout> | undefined;

        const clearRetry = () => {
            if (retryTimeout !== undefined) {
                clearTimeout(retryTimeout);
            }
            retryTimeout = undefined;
        };

        const clearFlush = () => {
            if (flushTimeout !== undefined) {
                clearTimeout(flushTimeout);
            }
            flushTimeout = undefined;
        };

        const isRunStale = (generation: number) => (
            cancelled || document.hidden || generation !== activeGeneration
        );

        // Re-runs the check after a non-answer while the tab stays visible, up to
        // AUTH_CHECK_MAX_RETRIES times. Stops immediately if the tab is hidden again or the hook
        // unmounts in the meantime — there's no point (or safety) in checking auth for a page the
        // viewer isn't looking at.
        const runWithRetry = async (attemptsLeft: number, generation: number) => {
            if (isRunStale(generation)) return;

            const needsRetry = await handleAuthCheck();

            if (isRunStale(generation) || !needsRetry || attemptsLeft <= 0) return;

            retryTimeout = setTimeout(() => {
                void runWithRetry(attemptsLeft - 1, generation);
            }, AUTH_CHECK_RETRY_DELAY_MS);
        };

        const throttledRun = throttle(
            (generation: number) => {
                void runWithRetry(AUTH_CHECK_MAX_RETRIES, generation);
            },
            SECONDS_TO_THROTTLE_AUTH_CHECK * 1000,
        );

        const invalidateActiveRun = () => {
            activeGeneration += 1;
            clearRetry();
            clearFlush();
        };

        const cancelPendingRuns = () => {
            invalidateActiveRun();
            throttledRun.cancel();
        };

        function handleVisibilityChange() {
            if (document.hidden) {
                // Cancels any retry scheduled from before the tab was hidden — resumes fresh
                // (full retry budget) the next time it becomes visible, rather than continuing a
                // countdown against a page nobody was looking at.
                cancelPendingRuns();
                return;
            }

            invalidateActiveRun();
            const generation = activeGeneration;

            throttledRun(generation);
            flushTimeout = setTimeout(() => {
                throttledRun.flush();
            }, SECONDS_TO_THROTTLE_AUTH_CHECK * 1000);
        }

        document.addEventListener('visibilitychange', handleVisibilityChange, false);
        return () => {
            cancelled = true;
            cancelPendingRuns();
            document.removeEventListener('visibilitychange', handleVisibilityChange, false);
        };
    }, [handleAuthCheck]);

    return { handleAuthCheck };
};
